import {AGENCY_KEYS} from "@/api/agencyApi";
import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import {INFLUENCER_KEYS} from "@/api/influencerApi";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {useSelectedAgency, useSelectedCompany, useUserId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import useCampaignFilterStore from "@/store/campaignFilter";
import {languagePreferenceAtom} from "@/store/jotaiAtoms";
import type {TLanguage, TPaymentMethod, TQueryOptions, TUserState} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {produce, type WritableDraft} from "immer";
import {useSetAtom} from "jotai";
import {useParams} from "react-router";

export const USER_KEYS = {
  mUser: (userId: number) => [`/users/${userId}/`],
  mUserAccountVerification: (search?: string) => [`/users/verify-account/${search}`],
  mUserAgreedPolicies: (userId: number) => [`/users/${userId}/agreed-policies/`],
  mUserEmail: (email: string) => [`/users/search_email/?email=${email}`],
  mUserInstagramUsernameValidation: ["/users/validate-username/"],
  mUserList: ["/users/"],
  mUserNewVerificationEmail: (userId: number) => [`/users/${userId}/new_verification_email/`],
  mUserNotificationListSeenStatus: (userId: number) => [`/users/${userId}/notifications/seen/`],
  mUserNotificationPreferences: (userId: number) => [`/users/${userId}/update_preference/`],
  mUserPasswordReset: (search?: string) => [`/users/reset-password/${search}`],
  mUserPasswordResetRequest: ["/users/reset-password-request/"],
  mUserPaymentMethodAttach: (userId: number) => [`/users/${userId}/attach_payment_card/`],
  mUserPaymentMethodDefault: (userId: number) => [`/users/${userId}/set_default_payment_method/`],
  mUserPaymentMethodDetach: (userId: number) => [`/users/${userId}/detach_payment_card/`],
  mUserReset: ["/users/reset/"],
  mUserTikTokUsernameValidation: ["/users/validate-tiktok-username/"],
  qUser: (userId: number) => [`/users/${userId}/`],
  qUserMobileAppRedirect: ["/users/mobile_app/"],
  qUserNotificationCounts: (userId: number) => [`/users/${userId}/notifications_count/`],
  qUserNotificationList: (userId: number) => [`/users/${userId}/notifications/`],
  qUserNotificationPreferences: (userId: number) => [`/users/${userId}/preferences/`],
  qUserPaymentMethods: (userId: number) => [`/users/${userId}/payment_cards/`],
  qUserUnagreedPolicies: (userId: number) => [`/users/${userId}/unagreed-policies/`],
} as const;

export const useUpdateUserLanguageMutation = () => {
  const {agencyId, campaignId, companyId, influencerId} = useParams<{
    agencyId?: string;
    campaignId?: string;
    companyId?: string;
    influencerId?: string;
  }>();
  const userId = useUserId();
  const selectedAgency = useSelectedAgency();
  const selectedCompany = useSelectedCompany();
  const setLanguagePreference = useSetAtom(languagePreferenceAtom);

  return useMutation({
    mutationKey: USER_KEYS.mUser(userId),
    mutationFn: ({lang}: {lang: TLanguage}) => client.patch(USER_KEYS.mUser(userId)[0], {language_code: lang}),
    onMutate: response => {
      queryClient.setQueryData<WritableDraft<AxiosResponse<TUserState>>>(
        USER_KEYS.qUser(userId),
        prevState =>
          prevState &&
          produce(prevState, draft => {
            draft.data.language_code = response.lang;
          }),
      );
      setLanguagePreference(response.lang);
    },
    onSuccess: () => {
      const DASHBOARD_CAMPAIGN_STATUSES = ["active", "drafts", "complete", "open", "paused"] as const;

      [
        CAMPAIGN_KEYS.qCampaign(Number(campaignId)),
        CAMPAIGN_KEYS.qCampaignList(useCampaignFilterStore.getState().searchParams),
        COMPANY_KEYS.qCompanyChatCampaigns(Number(selectedCompany?.id ?? companyId)),
        ...DASHBOARD_CAMPAIGN_STATUSES.map(status =>
          AGENCY_KEYS.qAgencyCampaigns({agencyId: Number(selectedAgency?.id ?? agencyId), status}),
        ),
        ...DASHBOARD_CAMPAIGN_STATUSES.map(status =>
          COMPANY_KEYS.qCompanyCampaigns({companyId: Number(selectedCompany?.id ?? companyId), status}),
        ),
        INFLUENCER_KEYS.qInfluencerActiveCampaigns(Number(influencerId)),
        INFLUENCER_KEYS.qInfluencerCompletedContent(Number(influencerId)),
        INFLUENCER_KEYS.qInfluencerCompletedUgcApplications(Number(influencerId)),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
    },
  });
};

export const useUpdateUserNotificationPreferencesMutation = ({
  companyId,
  isInitiallyEnabled,
  userId,
}: {
  companyId: number;
  isInitiallyEnabled: boolean;
  userId: number;
}) =>
  useMutation({
    mutationKey: USER_KEYS.mUserNotificationPreferences(userId),
    mutationFn: () =>
      client.post(USER_KEYS.mUserNotificationPreferences(userId)[0], {
        company_profile: companyId,
        email_enabled: !isInitiallyEnabled,
      }),
    onSuccess: () => {
      [COMPANY_KEYS.qCompanyMembers(companyId), USER_KEYS.qUserNotificationPreferences(userId)].map(queryKey =>
        queryClient.invalidateQueries({queryKey}),
      );
    },
  });

export const useUserMobileAppRedirectQuery = (params?: {enabled?: boolean}) =>
  useGenericQuery<{redirect?: string}>({
    queryKey: USER_KEYS.qUserMobileAppRedirect,
    enabled: params?.enabled ?? true,
  });

export const useUserPaymentMethodsQuery = (userId: number, queryOptions?: TQueryOptions<TPaymentMethod[]>) =>
  useGenericQuery<TPaymentMethod[]>({
    queryKey: USER_KEYS.qUserPaymentMethods(userId),
    enabled: !!userId,
    ...queryOptions,
  });
